import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';
import { visibleDestinations } from '../foundation/access';
import { AccountSettings } from '../foundation/account';
import { BackgroundTaskStatus } from '../foundation/background-task-chip';
import { BackgroundTasksProvider } from '../foundation/background-tasks';
import { hasExactPermission } from '../foundation/access';
import { COMPRAS_PERMISSIONS } from '../foundation/compras/permissions';
import { ComprasScanProvider } from '../foundation/compras/scan-host';
import { ComprasScanStatus } from '../foundation/compras/scan-status';
import { boardPathRedirect, IdentityChrome, NavigationShell, panelLocalViews, SessionBoundary, type IdentityChromeProps } from '../foundation/chrome';
import { createNoticeInboxPort } from '../foundation/inbox/live-port';
import { SessionInboxProvider } from '../foundation/inbox/session-inbox';
import { CLAIM_ACCOUNT_PATH, readInvitationToken, type ClaimAccountPort } from '../foundation/claim-account-link';
import { ClaimAccountScreen, CredentialRotationGate, MembershipPicker, preloadAccesoDoors } from '../foundation/acceso-lazy';
import { AccesoStage, type AccesoView } from '../foundation/acceso-door';
import { Band } from '../foundation/acceso-parts';
import { AccesoCannotOpen, AccesoRestoring, AccesoUnassigned } from '../foundation/acceso-states';
import { AccessLoginScreen } from '../foundation/credentials';
import { FrozenInteractionBoundary, InactivityWarning, SystemBanner } from '../foundation/state';
import { createClaimAccountPort } from '../runtime/claim-account-http';
import { IDENTITY_RUNTIME_OPERATION_NAMES, preloadOperationValidators } from '../runtime/identity-http';
import { PanelIdentityRuntime } from '../runtime/identity-runtime';
import { browserTabSessionTokenStore } from '../runtime/session-token-store';
import { type RuntimeUnavailableReason } from '../runtime/session-state';
import { sessionRoleLabel as roleLabel } from '../foundation/transport-copy';
import { durableWorkScopeOf, type PanelRuntimeSession } from '../runtime/session-model';
import { PANEL_SCREENS, panelScreenAtPath, panelScreenEntry, projectScreenAccess } from '../runtime/panel-screens';
import { RouteRecoveryStatus } from './route-recovery-boundary';
import type { RouteRecoveryAction } from './route-recovery-boundary';
import { accountBinding, preloadScreenAtPath, ScreenRuntime } from './screen-runtime';
import { useIdentitySession } from './use-identity-session';
import './runtime-boundary.scss';
import type { PanelGeneratedHttpPort, PanelLiveScreenId, PanelScreenConnection } from '../foundation/runtime-port';
import {
  SCREEN_CONNECTED_REPORT, ScreenConnectivityProvider, ShellOfflineProvider, type ScreenConnectivityReport,
} from '../foundation/shell-offline';
import { browserBootDevice, useScreenBootDoor, type ScreenBootDevicePort } from './screen-bindings/boot';

const FAILURE_COPY: Record<RuntimeUnavailableReason, string> = {
  network: 'No se ha podido conectar. Tu sesión y tus permisos no se han podido confirmar. Reintenta cuando vuelva la conexión.',
  permission: 'No tienes permiso para acceder a esta información. No se ha concedido acceso a ninguna área nueva.',
  server: 'El servidor no ha podido completar la operación. No se ha confirmado ningún cambio.',
  invalid_response: 'La respuesta del servidor no permite confirmar tu sesión y tus permisos. No se ha concedido acceso.',
  unassigned: 'Todavía no tienes un puesto asignado. Pide a tu responsable que te asigne a Servicio, Cocina o un departamento en el Constructor.',
};

/** @internal Exported only for its spec. */
export { loginMembershipDisabledReason } from '../foundation/acceso-picker-copy';

/** Release builds read the deployed API from env.js; local builds use the Vite value or the dev proxy. */
function panelApiBaseUrl(): string {
  return window.__ENV__?.API_URL && window.__ENV__?.API_VERSION
    ? `${window.__ENV__.API_URL}/${window.__ENV__.API_VERSION}`
    : import.meta.env.VITE_PANEL_API_BASE_URL || '/api/v1';
}

function createBrowserRuntime() {
  // Start loading the Identity reads' validators before the first identity request needs them, and the chunk of the
  // screen the address names while Identity answers (the build also modulepreloads the boot reads' validators).
  preloadOperationValidators(Object.values(IDENTITY_RUNTIME_OPERATION_NAMES));
  preloadScreenAtPath(window.location.pathname);
  // The rarer doors (the picker, the PIN, the invitation) load apart from the entry: at once on the invitation's
  // address, else once the browser is idle, long before a door can hand over to them.
  if (window.location.pathname === CLAIM_ACCOUNT_PATH) preloadAccesoDoors();
  else if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(preloadAccesoDoors, { timeout: 2_000 });
  else window.setTimeout(preloadAccesoDoors, 300);
  return new PanelIdentityRuntime({
    baseUrl: panelApiBaseUrl(),
    tokenStore: browserTabSessionTokenStore(),
  });
}

const INVENTORY_PATH = '/app/inventory';
type ComprasScanHttp = Pick<PanelGeneratedHttpPort, 'read' | 'mutate'>;
/** The scanning work's requests: on the Inventario client of the moment, so they follow a renewed session. */
const inventoryRequests = (runtime: PanelIdentityRuntime): ComprasScanHttp => ({
  read: (command) => runtime.screenClient('inventory').read(command),
  mutate: (command) => runtime.screenClient('inventory').mutate(command),
});

const homePath = (session: PanelRuntimeSession): string => session.tenant.kind === 'restaurant'
  ? session.tenant.access.navigationEnabled
    ? session.tenant.access.defaultRoute ?? visibleDestinations(session.tenant.access)[0]?.path ?? '/app/board' : '/app'
  : session.tenant.kind === 'franchise' ? '/app/franchise' : '/app/super-admin';

/** OD-1 (a), 2026-09-27: staff reach a workspace only through a Constructor
 * placement. The one explanation an unplaced member reads, whether Identity
 * refused the workspace read or answered with no area. */
const UNASSIGNED_TITLE = 'Todavía no tienes un puesto asignado';
const UNASSIGNED_BODY = 'Pide a tu responsable que te asigne a Servicio, Cocina o un departamento en el Constructor. En cuanto lo haga, vuelve a comprobarlo aquí.';

/** Every Panel destination is bound; this renders only for a restaurant
 * session whose access enables no navigation (its home path is `/app`). */
function NoAssignedAreas({ label, management }: { readonly label: string; readonly management: boolean }) {
  return <section className="runtime-screen-slot" data-screen-binding="unassigned" aria-label={label}>
    {management ? <>
      <h1 className="screen-title">No tienes áreas asignadas</h1>
      <SystemBanner>Tu acceso a este restaurante no incluye ninguna pantalla del Panel. Pide a la gerencia que te asigne un área.</SystemBanner>
    </> : <>
      <h1 className="screen-title">{UNASSIGNED_TITLE}</h1>
      <p className="runtime-unassigned__body">{UNASSIGNED_BODY}</p>
    </>}
  </section>;
}

/** Public invitation page: captures `#token=…` once, then removes it from history before any request. */
function ClaimAccountRoute({ port }: { readonly port: ClaimAccountPort }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [token] = useState(() => readInvitationToken(location.hash));
  useEffect(() => {
    if (location.hash) navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);
  return <ClaimAccountScreen onContinueToLogin={() => navigate('/login')} port={port} token={token} />;
}

/** Created only after Identity has projected a restaurant session. Creating a
 * screen client while Identity is still restoring would bind it to no scope. */
function RestaurantSessionInboxBoundary({ runtime, session, sessionActive, connection, children }: {
  readonly runtime: PanelIdentityRuntime;
  readonly session: PanelRuntimeSession;
  readonly sessionActive: boolean;
  readonly connection: PanelScreenConnection;
  readonly children: ReactNode;
}) {
  const navigate = useNavigate();
  const client = runtime.screenClient('inbox');
  const port = useMemo(() => createNoticeInboxPort(client), [client]);
  return <SessionInboxProvider connection={connection} port={port} principalKey={session.canonical.user.id}
    onNavigateHome={() => navigate('/app/home')} session={session.canonical} sessionActive={sessionActive} now={runtime.serverNow}>{children}</SessionInboxProvider>;
}

/* The navigation is the rail on every page and is never hidden: a screen's own «Mostrar el menú» is never asked for. */
const SCREEN_NAVIGATION = { hidden: false, onShow: () => undefined } as const;

type ScreenReports = Readonly<{
  screen: PanelLiveScreenId | null;
  connectivity: ScreenConnectivityReport;
  unsent: boolean;
  recovery: RouteRecoveryAction | null;
}>;
const noScreenReports = (screen: PanelLiveScreenId | null): ScreenReports => ({ screen, connectivity: SCREEN_CONNECTED_REPORT, unsent: false, recovery: null });

/** @internal Exported only for its spec. */
export function IdentityRuntimeBoundary({ runtime, hasUnsentWork = false, claimAccountPort, bootDevice = null }: {
  readonly runtime: PanelIdentityRuntime;
  readonly hasUnsentWork?: boolean;
  /** Injected by specs; the browser composition uses the generated public transport. */
  readonly claimAccountPort?: ClaimAccountPort;
  /** What a screen reads of this device at boot (`app/screen-bindings/boot.ts`); null reads none. */
  readonly bootDevice?: ScreenBootDevicePort | null;
}) {
  const state = useSyncExternalStore(runtime.subscribe, runtime.getSnapshot);
  const claimPort = useMemo(() => claimAccountPort ?? createClaimAccountPort({ baseUrl: panelApiBaseUrl() }), [claimAccountPort]);
  const location = useLocation();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  const [selectingMembership, setSelectingMembership] = useState<string | null>(null);
  const [membershipSelectionFailure, setMembershipSelectionFailure] = useState(false);
  const session = 'session' in state ? state.session : null;
  const tenant = session?.tenant;
  // One route lookup and one route admission (row 5.4a): a workspace screen
  // is a visible destination, Home/Board a local view, and a screen without a
  // workspace (Configuración, Franchise, Super-admin) its tenant's route.
  const screen = session ? panelScreenAtPath(location.pathname, session.tenant.kind) : null;
  // What the screen on the page reports to the shell, forgotten when the screen changes: its connectivity
  // (docs/decisions/2026-10-28-tpv-sin-conexion.md), its unsent work and its route recovery.
  const [reports, setReports] = useState<ScreenReports>(() => noScreenReports(screen));
  if (reports.screen !== screen) setReports(noScreenReports(screen));
  const reportConnectivity = useCallback((report: ScreenConnectivityReport) => {
    setReports((current) => current.connectivity.keepsWorking === report.keepsWorking
      && current.connectivity.holdsReauthentication === report.holdsReauthentication ? current : { ...current, connectivity: report });
  }, []);
  const setScreenHasUnsentWork = useCallback((unsent: boolean) => { setReports((current) => ({ ...current, unsent })); }, []);
  const setRouteRecovery = useCallback((recovery: RouteRecoveryAction | null) => { setReports((current) => ({ ...current, recovery })); }, []);
  const screenReport = reports.screen === screen ? reports : noScreenReports(screen);
  const routeRecovery = screenReport.recovery;
  const unsentWork = hasUnsentWork || screenReport.unsent;
  const feedback = useIdentitySession(runtime, state, location.pathname, unsentWork);

  const bootDoor = useScreenBootDoor({
    device: bootDevice,
    identity: state.kind === 'restoring' ? 'restoring' : state.kind === 'signed_out' ? 'signed_out'
      : state.kind === 'ready' ? 'ready' : state.kind === 'unavailable' && session === null && state.reason !== 'unassigned' ? 'no-session' : 'other',
    owner: state.kind === 'signed_out' || state.kind === 'expired' || (state.kind === 'credential_required' && state.status === 'expired') ? null
      : state.kind === 'ready' ? state.session.canonical.user.id : undefined,
    restaurantId: tenant?.kind === 'restaurant' ? tenant.id : null,
    online: feedback.online,
    onReturn: () => { if (state.kind === 'unavailable') void runtime.rehydrateSession(); },
  });

  const access = tenant?.kind === 'restaurant' ? tenant.access : null;
  // A screen's exact permissions are projected for that screen: the session's own list does not name them.
  const scanAccess = access === null ? null : projectScreenAccess(access, 'inventory');
  const destinations = access ? visibleDestinations(access) : [];
  const localViews = access ? panelLocalViews(access) : [];
  const safePath = session ? homePath(session) : '/app';
  // A manager's Board opens Analítica: the page selector's own path sends there instead of being refused.
  const boardRedirect = location.pathname === '/app/board' ? boardPathRedirect(localViews) : null;
  const isAccountPath = location.pathname === '/app/config';
  // Restaurant Configuración already composes Mi cuenta with the admitted
  // local-settings owners. Non-restaurant sessions retain the standalone
  // account leaf when they arrive through the shared identity chrome.
  const isAccountOnly = isAccountPath && tenant?.kind !== 'restaurant';
  const screenRow = screen === null ? null : PANEL_SCREENS[screen];
  const screenEntry = screen === null ? null : panelScreenEntry(screen);
  const screenAdmitted = screenRow !== null && ('workspaceId' in screenRow
    ? destinations.some(({ path }) => path === location.pathname)
    : 'navigationEnabled' in screenRow ? localViews.some(({ path }) => path === location.pathname) : true);
  const hasPrimaryAction = screenEntry?.hasPrimaryAction === true;
  const pathAllowed = isAccountOnly || destinations.some(({ path }) => path === location.pathname)
    || localViews.some(({ path }) => path === location.pathname) || screenAdmitted;

  /* What the shell hands the screen is memoized, so a shell-only change (a banner, the frozen boundary, the idle
     warning, a re-check that confirmed the same session) does not re-render it: only a change of what it receives
     does. The callbacks read the shell's latest frozen state through a ref. */
  const shellFrozen = useRef(true);
  const screenActive = state.kind === 'ready';
  // A screen that keeps working offline is never frozen, whatever the session (ready, expired, or unavailable after a
  // failed foreground re-check), and no general line is drawn over it: it draws its own banner.
  const screenWorking = screenReport.connectivity.keepsWorking;
  const screenOffline = screenWorking || (screenEntry?.drawsOwnOffline === true && screenActive && !feedback.online);
  const screenConnection: PanelScreenConnection = !screenActive || !feedback.online ? 'reconnecting'
    : feedback.verifying ? 'verifying' : 'online';
  const screenFrozen = !screenOffline && (!screenActive || !feedback.online || feedback.verifying);
  // The reauthentication sheet waits while the screen holds it (its own report: the Wi-Fi often stays up through an outage).
  const reauthenticationOnline = feedback.online && !screenReport.connectivity.holdsReauthentication;
  useLayoutEffect(() => { shellFrozen.current = screenFrozen; }, [screenFrozen]);
  const navigateFromScreen = useCallback((path: string) => {
    if (!shellFrozen.current) { setDenied(false); navigate(path); }
  }, [navigate]);
  const pathname = location.pathname;
  const replaceSearch = useCallback((search: string) => navigate({ pathname, search }, { replace: true }), [navigate, pathname]);
  const screenElement = useMemo(() => session !== null && screen !== null && screenAdmitted
    ? <ScreenConnectivityProvider value={reportConnectivity}><ShellOfflineProvider value={screenOffline}><ScreenRuntime path={pathname} runtime={runtime} screen={screen}
      session={session} sessionActive={screenActive} connection={screenConnection}
      navigation={SCREEN_NAVIGATION} onNavigate={navigateFromScreen}
      onUnsentWorkChange={setScreenHasUnsentWork} onRouteRecoveryChange={setRouteRecovery}
      search={location.search} onSearchChange={replaceSearch} /></ShellOfflineProvider></ScreenConnectivityProvider> : null,
  [location.search, navigateFromScreen, pathname, replaceSearch, reportConnectivity, runtime, screen, screenActive, screenAdmitted,
    screenConnection, screenOffline, session, setRouteRecovery, setScreenHasUnsentWork]);

  useEffect(() => {
    if (state.kind !== 'ready' || location.pathname === CLAIM_ACCOUNT_PATH) return;
    const entry = location.pathname === '/' || location.pathname === '/login' || location.pathname === '/app';
    if (entry && location.pathname !== safePath) { setDenied(false); navigate(safePath, { replace: true }); }
    else if (boardRedirect !== null) { setDenied(false); navigate(boardRedirect, { replace: true }); }
    else if (!entry && !pathAllowed && location.pathname !== safePath) { setDenied(true); navigate(safePath, { replace: true }); }
  }, [boardRedirect, location.pathname, navigate, pathAllowed, safePath, state.kind]);

  useEffect(() => {
    if (session === null) setRouteRecovery(null);
  }, [session, setRouteRecovery]);

  // Every door of the access family and the Panel share one stage: the door stays while its content changes, and the
  // hand-over into the Panel (and back) plays between them (foundation/acceso-door.tsx).
  const stage = (view: AccesoView | null, door: ReactNode, panel: ReactNode = null) => <AccesoStage door={door} view={view}>{panel}</AccesoStage>;
  // The invitation link is public: it renders before, and independently of, any session state.
  if (location.pathname === CLAIM_ACCOUNT_PATH) return stage('claim', <ClaimAccountRoute port={claimPort} />);
  if (state.kind === 'restoring' || bootDoor.pending) return stage('restoring', <AccesoRestoring />);
  // Before the cannot-open stage: a screen's own door over a session this tab holds but cannot confirm.
  if (bootDoor.stage !== null) return <Suspense fallback={stage('restoring', <AccesoRestoring />)}>{bootDoor.stage}</Suspense>;
  if (state.kind === 'signed_out') return stage('login', <AccessLoginScreen onAuthenticate={runtime.authenticate} onSecondFactorRequired={() => undefined}
    {...(state.localOnly ? { notice: 'Se ha retirado la sesión caducada de este Panel. No se ha confirmado su revocación en el servidor.' } : {})}
    {...(bootDoor.loginBand === null ? {} : { tabletBand: bootDoor.loginBand })} />);
  if (state.kind === 'membership_selection_required') {
    const selectMembership = async (membershipId: string) => {
      setSelectingMembership(membershipId); setMembershipSelectionFailure(false);
      const outcome = await runtime.selectLoginMembership(membershipId);
      if (outcome.kind === 'unavailable') setMembershipSelectionFailure(true);
      setSelectingMembership(null);
    };
    return stage('picker', <MembershipPicker failure={membershipSelectionFailure} memberships={state.memberships} onLogout={runtime.restartLogin}
      onSelect={(membershipId) => void selectMembership(membershipId)} personName={state.personName} selecting={selectingMembership} />);
  }

  if (state.kind === 'credential_required') {
    const { credential } = state;
    const expired = state.status === 'expired';
    const logoutFailed = state.status === 'unavailable' && state.operation === 'logout';
    // A failed current read does not undo an acknowledged credential receipt.
    const verificationFailure = state.status === 'unavailable' && state.reason === 'server'
      ? 'No se ha podido comprobar tu sesión. El Panel sigue cerrado hasta recibir una respuesta válida.'
      : state.status === 'unavailable' ? FAILURE_COPY[state.reason] : '';
    return stage('rotation', <SessionBoundary avatarUrl={credential.canonical.user.avatar_url ?? null} expired={expired && feedback.online} personName={credential.personName} roleLabel=""
      onAuthenticate={runtime.reauthenticate} onSecondFactorRequired={() => undefined} onDifferentPerson={runtime.restartLogin}>
      <CredentialRotationGate key={`${credential.canonical.user.id}:${credential.tenant.kind}:${credential.tenant.id}`}
        personName={credential.personName} avatarUrl={credential.canonical.user.avatar_url ?? null} contextLabel={credential.tenant.name}
        frozen={state.status !== 'required' || !feedback.online || feedback.verifying} sessionIssue={state.status === 'unavailable'}
        verifyLabel={logoutFailed ? 'Reintentar el cierre de sesión' : 'Volver a comprobar mi sesión'}
        onRotate={runtime.completeCredentialRotation} onLogout={runtime.logout}
        onVerify={logoutFailed ? runtime.logout : runtime.rehydrateSession}>
        {state.status === 'unavailable' && <Band role="alert" tone="critical">{logoutFailed ? 'No se ha podido confirmar el cierre de sesión. ' : ''}{verificationFailure}</Band>}
        {!feedback.online && <Band role="alert" tone="caution">{expired ? 'Tu sesión ha caducado. ' : ''}No hay conexión. El Panel sigue cerrado; confirma tu identidad cuando vuelva.</Band>}
        {feedback.online && feedback.verifying && <Band breathing role="status" tone="info">Confirmando tu sesión. El Panel sigue cerrado.</Band>}
      </CredentialRotationGate>
    </SessionBoundary>);
  }

  const retrying = () => state.kind === 'unavailable' && state.operation === 'logout' ? runtime.logout()
    : state.kind === 'unavailable' && state.operation === 'renew' ? runtime.renew() : runtime.rehydrateSession();
  const retry = () => { void retrying(); };
  const failure = state.kind === 'unavailable' && !screenWorking ? <div className="runtime-failure" data-runtime-error={state.reason}>
    <SystemBanner tone="critical" urgent>{state.operation === 'logout' ? 'No se ha podido confirmar el cierre de sesión. ' : ''}{FAILURE_COPY[state.reason]}</SystemBanner>
    <button className="btn btn--secondary" onClick={retry} type="button">Reintentar</button>
  </div> : null;
  if (!session && state.kind === 'unavailable' && state.reason === 'unassigned') {
    return stage('unassigned', <AccesoUnassigned onLogout={() => void runtime.logout()} onRecheck={retrying} />);
  }
  if (!session || !tenant) return stage('cannot-open', <AccesoCannotOpen onLogout={() => void runtime.logout()} onRetry={retrying}
    retry={state.kind === 'unavailable'}
    notice={state.kind === 'unavailable' && state.verifiedCredentialScope
      ? <Band role="status" tone="info">Tu sesión ya no requiere cambiar el PIN. El acceso al Panel sigue cerrado.</Band> : null}
    failure={state.kind === 'unavailable' ? <div className="runtime-failure" data-runtime-error={state.reason}>
      <Band role="alert" tone="critical">{state.operation === 'logout' ? 'No se ha podido confirmar el cierre de sesión. ' : ''}{FAILURE_COPY[state.reason]}</Band>
    </div> : null} />);

  const expired = state.kind === 'expired';
  /* A screen that draws its own «Sin conexión» is neither frozen nor covered by the general sentence while
     the only trouble is the connection: it stays readable and navigable, and the Identity runtime still refuses every
     operation offline. An expired session or a re-check freezes it as any other (`foundation/shell-offline.ts`). */
  const ownOffline = screenOffline;
  const frozen = screenFrozen;
  // docs/decisions/2026-10-06-panel-read-runtime.md · a foreground re-check
  // pauses actions (`frozen`) but is not a disconnect for the screen's reads.
  const connection = screenConnection;
  const identity: IdentityChromeProps = {
    personName: session.personName, avatarUrl: session.canonical.user.avatar_url ?? null, restaurantName: tenant.name, roleLabel: roleLabel(session),
    summaryName: session.canonical.user.name?.trim().split(/\s+/)[0] || session.personName, identifier: session.identifier,
    onChangePerson: () => void runtime.logout(), onLogout: () => void runtime.logout(),
    onOpenAccountSettings: () => { if (!frozen) navigate('/app/config'); }, showRestaurantSwitcher: false, showChangePerson: false,
    showNotices: tenant.kind === 'restaurant',
  };
  const activeDestination = destinations.find(({ path }) => path === location.pathname);
  const stageLabel = activeDestination?.label ?? (location.pathname === '/app/board' ? 'Board'
    : location.pathname === '/app/home' ? 'Home' : location.pathname === '/app/config' ? 'Configuración'
      : tenant.kind === 'franchise' ? 'Franquicia' : tenant.kind === 'platform' ? 'Exylon' : 'Panel');
  const screenRuntime = screenElement;
  const runtimeStatus = <>
    {failure}
    {bootDoor.band}
    {/* docs/decisions/2026-10-08-ai-photo-enhancement-runs-in-the-background-of-the-panel.md: one line above every screen. */}
    <BackgroundTaskStatus disabled={frozen} onNavigate={(path) => { if (!frozen) { setDenied(false); navigate(path); } }} />
    {/* docs/decisions/2026-11-02-compras-scan-and-go.md: the tickets on their way, anywhere but Inventario. */}
    {scanAccess !== null && hasExactPermission(scanAccess, COMPRAS_PERMISSIONS.read) && <ComprasScanStatus disabled={frozen} here={location.pathname === INVENTORY_PATH}
      onShow={() => { if (!frozen) { setDenied(false); navigate(INVENTORY_PATH); } }} ready={state.kind === 'ready' && feedback.online} />}
    <div data-route-recovery-control="" hidden={routeRecovery === null}>
      <RouteRecoveryStatus action={routeRecovery} />
    </div>
    {feedback.idleWarning && <InactivityWarning onStay={feedback.stayActive} />}
    {!expired && !feedback.online && !ownOffline && <SystemBanner tone="caution" urgent>No hay conexión. Las acciones están paradas; lo que tenías abierto sigue a la vista.</SystemBanner>}
    {!failure && !screenWorking && feedback.online && feedback.verifying && <SystemBanner>Confirmando tu sesión y tus permisos. Las acciones están paradas hasta recibir la respuesta.</SystemBanner>}
    {expired && !screenWorking && !feedback.online && <SystemBanner tone="caution" urgent>Tu sesión ha caducado. {unsentWork
      ? 'No hay conexión y tienes una ronda escrita sin enviar.' : 'No hay conexión, así que ahora no podemos confirmarlo.'} La pantalla queda en solo lectura y tu trabajo sigue aquí: no lo cierres. Te pediremos la contraseña en cuanto se pueda.</SystemBanner>}
  </>;
  const ownsCompleteShell = screen === 'franchise' || screen === 'super-admin';

  // The sheet is offered whenever there is a connection, unsent work or not:
  // reauthenticating the same person keeps this screen, its draft and its
  // retained commands (docs/decisions/2026-09-30-panel-reauthentication-keeps-unsent-work.md).
  // Home and the Board keep the plain shell they have always drawn (no fixed menu button in a narrow window).
  const shellSkin = screenEntry?.plainShell === true ? {} : { className: 'runtime-shell', drawerClassName: 'runtime-shell__drawer' };
  const readyBoundary = <SessionBoundary avatarUrl={session.canonical.user.avatar_url ?? null} expired={expired && reauthenticationOnline} personName={session.personName}
    roleLabel={roleLabel(session)} onAuthenticate={runtime.reauthenticate} onSecondFactorRequired={() => undefined} onDifferentPerson={runtime.restartLogin}>
    {ownsCompleteShell && screenRuntime ? <div data-runtime-tenant={tenant.kind} data-screen-binding={screen}>
      {runtimeStatus}
      {denied && <SystemBanner>Esa página no está en tu acceso. Te hemos traído a {stageLabel}.</SystemBanner>}
      <FrozenInteractionBoundary frozen={frozen} showBanner={false}>{screenRuntime}</FrozenInteractionBoundary>
    </div> :
    <NavigationShell access={access} bottomNavigation={Boolean(access)} {...shellSkin} contentAttributes={{ 'data-screen-binding': pathAllowed && access ? screen ?? undefined : undefined }}
      currentPath={location.pathname} drawerTitle={tenant.name} frozen={frozen}
      hasPrimaryAction={hasPrimaryAction} identity={identity} localViews={localViews}
      onNavigate={(path) => { if (!frozen) { setDenied(false); navigate(path); } }} restaurantName={tenant.name} status={runtimeStatus}
      tenantChrome={<aside className="runtime-tenant-chrome"><strong className="restaurant-name">{tenant.name}</strong><IdentityChrome {...identity} /></aside>}
      tenantKind={tenant.kind}>
      {denied && <SystemBanner>Esa página no está en tu acceso. Te hemos traído a {stageLabel}.</SystemBanner>}
      {isAccountOnly ? <AccountSettings {...accountBinding(runtime, session)}
        accessContent={<p>{access ? destinations.map(({ label }) => label).join(' · ') : roleLabel(session)}</p>} />
        : boardRedirect !== null ? null
        : pathAllowed || location.pathname === safePath ? screenRuntime ?? <NoAssignedAreas label={stageLabel} management={access?.navigationRole === 'gerencia'} />
        : <SystemBanner>Esa página no está en tu acceso y no hay un área concedida disponible.</SystemBanner>}
    </NavigationShell>}
  </SessionBoundary>;
  // The background tasks outlive every screen of one principal in one restaurant, never a change of either.
  return stage(null, null, tenant.kind === 'restaurant' ? <RestaurantSessionInboxBoundary connection={connection}
    runtime={runtime} session={session} sessionActive={state.kind === 'ready'}>
    <BackgroundTasksProvider key={`${session.canonical.user.id}:${tenant.id}`} scope={durableWorkScopeOf(session)}>
      <ComprasScanProvider http={inventoryRequests(runtime)} key={`${session.canonical.user.id}:${tenant.id}`} scope={durableWorkScopeOf(session)}>{readyBoundary}</ComprasScanProvider>
    </BackgroundTasksProvider>
  </RestaurantSessionInboxBoundary>
    : readyBoundary);
}

export function RuntimeBoundary() {
  const [runtime] = useState(createBrowserRuntime);
  const [bootDevice] = useState(() => browserBootDevice(runtime));
  return <BrowserRouter><IdentityRuntimeBoundary runtime={runtime} bootDevice={bootDevice} /></BrowserRouter>;
}
